import { Check, Link2, Megaphone } from 'lucide-react';
import { Input } from '@r/components/ui/input.tsx';
import { Textarea } from '@r/components/ui/textarea.tsx';
import { ColorPicker } from '@r/components/ui/color-picker.tsx';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@r/components/ui/select.tsx';
import { cn } from '@r/utils/utils.ts';
import { getWidgetSelectOptionLabel } from '@admin/pages/personalization/content-block-select-labels.ts';
import {
  isSlideFieldRequired,
  resolveSelectFieldValue
} from '@admin/pages/personalization/content-block-slides.ts';
import { ContentBlockMediaField } from '@admin/components/personalization/content-block-media-field.tsx';
import {
  colorsMatchHsl,
  FILTER_PRESETS,
  hslCoreToPickerValue,
  resolveFilterPresetValue,
  slideFormInputClassName,
  type ContentBlockSlideFieldControlProps
} from '@admin/components/personalization/content-block-slide-form-field-utils.ts';

/** Contrôle de saisie d’une définition widget (couleur, média, select, etc.). */
export const ContentBlockSlideFieldControl = ({
  itemDefinition,
  value,
  onChange,
  t,
  textColorPresets,
  progresses,
  mediaFieldDisabled,
  onMediaUpload
}: ContentBlockSlideFieldControlProps) => {
  if (itemDefinition.itemType === 'hsl_color') {
    const rawValue = (value as string) || 'hsl(0, 0%, 100%)';
    const pickerValue = rawValue.startsWith('hsl') ? rawValue : hslCoreToPickerValue(rawValue);
    const matchedPreset = textColorPresets.find((preset) => colorsMatchHsl(preset.value, rawValue));

    return (
      <div className="space-y-3">
        <div className="flex flex-wrap gap-3 items-center">
          <ColorPicker
            value={pickerValue}
            onChange={onChange}
            className="w-11 h-11 rounded-full shrink-0"
          />
          {textColorPresets.map((preset) => {
            const isSelected = colorsMatchHsl(preset.value, rawValue);
            return (
              <button
                key={preset.id}
                type="button"
                aria-label={preset.label}
                onClick={() => onChange(preset.value)}
                className={cn(
                  'relative h-11 w-11 rounded-full border-2 transition-transform hover:scale-105',
                  isSelected ? 'border-primary ring-2 ring-primary/30' : 'border-border/60',
                  preset.id === 'white' && 'bg-white'
                )}
                style={
                  preset.id === 'white' ? undefined : { backgroundColor: preset.cssBackground }
                }
              >
                {isSelected ? (
                  <Check
                    className={cn(
                      'absolute inset-0 m-auto h-5 w-5 drop-shadow-sm',
                      preset.id === 'black' ? 'text-white' : 'text-primary'
                    )}
                    strokeWidth={3}
                    aria-hidden
                  />
                ) : null}
              </button>
            );
          })}
        </div>
        <div className="flex gap-2 items-center px-3 py-2 max-w-xs rounded-lg border border-border/60 bg-muted/20">
          <span
            className="w-6 h-6 rounded-md border shrink-0 border-border/50"
            style={{ backgroundColor: pickerValue }}
            aria-hidden
          />
          <Input
            type="text"
            value={matchedPreset?.label ?? rawValue}
            className="flex-1 p-0 h-8 text-xs bg-transparent border-0 shadow-none focus-visible:ring-0"
            readOnly
          />
        </div>
      </div>
    );
  }

  if (itemDefinition.itemType === 'media') {
    return (
      <ContentBlockMediaField
        value={value}
        progresses={progresses}
        disabled={mediaFieldDisabled}
        onValueChange={(files) => {
          void onMediaUpload(itemDefinition.itemKey, files, onChange as (v: string) => void);
        }}
        onRemove={() => {
          void onMediaUpload(itemDefinition.itemKey, [], onChange as (v: string) => void);
        }}
      />
    );
  }

  if (itemDefinition.itemKey === 'filter_image') {
    const currentValue = resolveFilterPresetValue(value);

    return (
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {FILTER_PRESETS.map((preset) => {
          const isSelected = currentValue === preset.value;
          return (
            <button
              key={preset.value}
              type="button"
              onClick={() => onChange(preset.value)}
              className={cn(
                'p-4 text-left rounded-xl border-2 transition-colors',
                isSelected
                  ? 'shadow-sm border-primary bg-primary/5'
                  : 'border-border/70 bg-muted/25 hover:border-border hover:bg-muted/40'
              )}
            >
              <p className="text-sm font-semibold">{t(preset.labelKey)}</p>
              <p
                className={cn(
                  'mt-1 text-xs',
                  preset.recommended ? 'font-medium text-primary' : 'text-muted-foreground'
                )}
              >
                {t(preset.subtitleKey)}
              </p>
            </button>
          );
        })}
      </div>
    );
  }

  if (itemDefinition.itemType === 'select') {
    const options = itemDefinition.options ?? [];
    const selectedValue = resolveSelectFieldValue(itemDefinition, value);

    return (
      <Select value={selectedValue} onValueChange={onChange} disabled={options.length === 0}>
        <SelectTrigger className={cn('w-full', slideFormInputClassName)}>
          <SelectValue
            placeholder={
              itemDefinition.itemPlaceholder || t('admin.contentblock.editor.select.placeholder')
            }
          />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option} value={option}>
              {getWidgetSelectOptionLabel(itemDefinition.itemKey, option, t)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  if (itemDefinition.itemKey === 'description') {
    return (
      <Textarea
        value={(value as string) || ''}
        onChange={(event) => onChange(event.target.value)}
        placeholder={itemDefinition.itemPlaceholder || ''}
        className={cn('resize-y min-h-[128px]', slideFormInputClassName)}
        required={isSlideFieldRequired(itemDefinition)}
        maxLength={itemDefinition.maxLength || undefined}
      />
    );
  }

  const inputIcon =
    itemDefinition.itemKey === 'text_cta' ? (
      <Megaphone className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
    ) : itemDefinition.itemKey === 'extern_link' ? (
      <Link2 className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
    ) : null;

  return (
    <div className="relative">
      {inputIcon}
      <Input
        value={value as string}
        onChange={(event) => onChange(event.target.value)}
        type={itemDefinition.itemType === 'url' ? 'url' : 'text'}
        placeholder={itemDefinition.itemPlaceholder || ''}
        className={cn('w-full', slideFormInputClassName, inputIcon && 'pl-10')}
        required={isSlideFieldRequired(itemDefinition)}
        maxLength={itemDefinition.maxLength || undefined}
      />
    </div>
  );
};
